import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  NgZone,
  OnInit,
  output,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { translation } from '@cue/translations';
import { ReactiveFormsModule } from '@angular/forms';
import { LoadingComponent } from '@cue/loading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AssistPropertyBM, LocalizedString, OpenDetailFromEnum, SignalRService } from '@cue/api';
import {
  cueArrowFullLeft,
  cueArrowRight,
  cueCalendar,
  cueCheck,
  cueClose,
  cueNavigationCircle,
  cuePanorama,
  cueQrCode,
  cueRefresh,
  cueShare,
  cueStart,
  cueTarget,
  IconComponent,
} from '@cue/ui/icons';
import {
  CurrentMapItemService,
  FloorplanLayoutEnum,
  NavigationDetailInfo,
  NavigationService,
  QrIdListPipe,
  ResourceSchedule,
} from '@cue/navigation-services';
import { HasScrollbarDirective, ImageWidthPipe, LocalizedStringPipe } from '@cue/utilities';
import { interval, takeUntil } from 'rxjs';
import { ButtonComponent, ControllableObjectsComponent, WorkingHoursComponent } from '@cue/ui';
import { CustomUnsafeSanitizePipe } from '@cue/security';
import { localized } from '@cue/translate';
import { ActivatedRoute, Params } from '@angular/router';
import { IResourceIdentification } from '../resources-detail/resources-detail.component';
import { FileGalleryComponent } from '@cue/ui/file-gallery';
import { PoisComponent } from '@cue/poi';
import {
  AreaUnavailableNotice,
  ChangeAreaSourceEnum,
  RequestAreaChangeData,
  ResourcePoiIdentificationCrate,
} from '@cue/data';
import { NAVIGATION_SETTINGS } from '@cue/navigation-settings';
import { AsyncPipe, DatePipe, NgStyle } from '@angular/common';
import { rxResource } from '@angular/core/rxjs-interop';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { UnitDetailComponent } from '../unit-detail/unit-detail.component';
import { PoisCanBeShownInResourcePipe } from '../../pipes/pois-can-be-shown-in-resource.pipe';

export enum DetailViewState {
  RequestClose,
  RequestChangeStart,
  RequestChangeTarget,
}

export interface MapItemDetailRequest {
  viewState: DetailViewState;
  itemData?: NavigationDetailInfo;
}

@Component({
  selector: 'assist-map-item-detail',
  templateUrl: './map-item-detail.component.html',
  styleUrls: ['./map-item-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    ReactiveFormsModule,
    LoadingComponent,
    IconComponent,
    ImageWidthPipe,
    ButtonComponent,
    WorkingHoursComponent,
    CustomUnsafeSanitizePipe,
    LocalizedStringPipe,
    ControllableObjectsComponent,
    FileGalleryComponent,
    PoisCanBeShownInResourcePipe,
    PoisComponent,
    QrIdListPipe,
    UnitDetailComponent,
    AsyncPipe,
    NgStyle,
    DatePipe,
    HasScrollbarDirective,
  ],
  animations: [
    trigger('slideInTop', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-50%)' }),
        animate('300ms', style({ opacity: 1, transform: 'translateY(0%)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateY(0%)' }),
        animate('300ms', style({ opacity: 0, transform: 'translateY(-50%)' })),
      ]),
    ]),
    trigger('opacity', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('300ms', style({ opacity: 0 }))]),
    ]),
    trigger('scale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate(
          '300ms',
          style({
            opacity: 1,
            transform: 'scale(1)',
          })
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate(
          '300ms',
          style({
            opacity: 0,
            transform: 'scale(0)',
          })
        ),
      ]),
    ]),
  ],
})
export class MapItemDetailComponent implements OnInit {
  private navigationService = inject(NavigationService);
  private translocoService = inject(TranslocoService);
  private currentMapItemService = inject(CurrentMapItemService, { optional: true });
  protected activatedRoute = inject(ActivatedRoute);
  private signalRService = inject(SignalRService);
  private zone = inject(NgZone);
  // private configService = inject(FloorplanConfigService);
  protected readonly localized = localized;

  ICONS = {
    cueQrCode,
    cueNavigationCircle,
    cueClose,
    cueStart,
    cueTarget,
    cueArrowRight,
    cueArrowFullLeft,
    cueRefresh,
    cueCheck,
    cuePanorama,
    cueShare,
    cueCalendar,
  };

  translation = translation;

  // protected parentItemId: number;
  isResource = computed(() => {
    return this.resourcePoiIdentification()?.resourceId != null;
  });

  closeOnly = input<boolean, boolean | 'true' | 'false'>(false, {
    transform: booleanAttribute,
  });
  openDetailFrom = input<OpenDetailFromEnum>(OpenDetailFromEnum.None);

  mapAppLayout = input<FloorplanLayoutEnum | null>();

  assistUrl = input<string>();

  startSelectionPriority = computed(() => {
    return this.openDetailFrom() === OpenDetailFromEnum.StartSelection;
  });

  isReservableResource = computed(() => {
    return this.isResource() && this.itemDetail.hasValue() && this.itemDetail.value().isReservable;
  });

  showRouteIsVisible = computed(() => {
    return (
      (this.kioskId() !== null || this.appId() !== null) &&
      !this.itemDetail.value().currentFloorplanApp &&
      this.itemDetail.value().setAsTarget &&
      this.mapAppLayout() !== FloorplanLayoutEnum.Reservation
    );
  });

  reservationUrl = computed(() => {
    let result = null;
    if (this.itemDetail.hasValue() && this.assistUrl()) {
      const qrCodeId =
        this.itemDetail.value()?.qrCodeId ||
        (this.itemDetail.value()?.qrCodes.length > 0 ? this.itemDetail.value()?.qrCodes[0].qrCodeId : null);
      if (qrCodeId) {
        const resourceName = this.mapItemName(this.itemDetail.value());
        const params = new URLSearchParams({
          tab: 'events',
          resourceName,
        });
        result = `${this.assistUrl()}/qr/${qrCodeId}?${params.toString()}`;
      }
    }
    return result;
  });

  kioskId = input<string | null>(null);
  appId = input<string | null>(null);
  areaUnavailableNotice = input<AreaUnavailableNotice | null>(null);
  resourcePoiIdentification = model<ResourcePoiIdentificationCrate>();
  closePanel = output<void>();
  itemDetailResult = output<MapItemDetailRequest>();
  requestMoveToArea = output<RequestAreaChangeData>();
  poiOrResourceDetailOpened = output<IResourceIdentification | null>();

  private readonly reloadResourceDetailWhenAreaAvailabilityChanged = explicitEffect(
    [this.areaUnavailableNotice],
    ([areaUnavailableNotice]) => {
      if (
        !areaUnavailableNotice ||
        !this.itemDetail.hasValue() ||
        this.itemDetail.value().areaId !== areaUnavailableNotice.invalidAreaId
      ) {
        return;
      }

      this.itemDetail.reload();
    },
    { defer: true }
  );

  navigationSettings = inject(NAVIGATION_SETTINGS);

  terminator = injectDestroy();
  ngOnInit() {
    this.signalRService
      .listen<string>('reload', true, '/MessageHub')
      .pipe(takeUntil(this.terminator))
      .subscribe((qrCodeId) => {
        this.handleData(qrCodeId);
      });

    this.signalRService
      .listen<string>('reload-chaneged-qrCode', false, '/MessageHub')
      .pipe(takeUntil(this.terminator))
      .subscribe((qrCodeId) => {
        this.handleData(qrCodeId);
      });

    this.signalRService
      .listen<number>('reload-changed-resource', false, '/MessageHub')
      .pipe(takeUntil(this.terminator))
      .subscribe((resourceId) => {
        if (this.resourcePoiIdentification()?.resourceId && resourceId == this.resourcePoiIdentification().resourceId) {
          this.itemDetail.reload();
        }
      });

    this.zone.runOutsideAngular(() => {
      interval(30000)
        .pipe(takeUntil(this.terminator))
        .subscribe(() => this.scheduleDetail.reload());
    });
  }

  private handleData(qrCodeId: string) {
    if (this.resourcePoiIdentification()?.qrCodeId && qrCodeId == this.resourcePoiIdentification().qrCodeId) {
      this.itemDetail.reload();
    }
  }

  itemDetailParams = computed(
    () => ({
      kioskId: this.kioskId(),
      appId: this.appId(),
      resourceId: this.resourcePoiIdentification().resourceId,
      qrCodeId: this.resourcePoiIdentification().qrCodeId,
    }),
    {
      equal: (a, b) =>
        a.appId === b.appId && a.kioskId === b.kioskId && a.resourceId === b.resourceId && a.qrCodeId === b.qrCodeId,
    }
  );

  itemDetail = rxResource({
    params: () => this.itemDetailParams(),
    stream: ({ params }) => this.navigationService.getNavigationDetailWithScreenshot(params),
  });

  scheduleParams = computed(() => {
    if (!this.itemDetail.hasValue()) {
      return undefined;
    }

    const resourceId = this.itemDetail.value()?.resourceId;
    if (!resourceId) {
      return undefined;
    }

    return {
      resourceId,
      appId: this.appId(),
      kioskId: this.kioskId(),
    };
  });

  scheduleDetail = rxResource({
    params: () => this.scheduleParams(),
    stream: ({ params }) => this.navigationService.getNavigationSchedule(params),
  });

  protected openPoiDetail(qrCodeId: string) {
    this.resourcePoiIdentification.set({
      resourceId: null,
      qrCodeId: qrCodeId,
    });
    this.currentMapItemService?.updateCurrentItemId(null, qrCodeId);
    this.poiOrResourceDetailOpened.emit({
      resourceId: null,
      qrCodeId: qrCodeId,
    });
  }

  protected getSimpleFiltersByResourceTypeId(detail: NavigationDetailInfo) {
    return this.getFiltersFromNavigationDetail(detail).filter((x) => x.dataTypeId !== 5);
  }

  protected getEnumerableFiltersByResourceTypeId(detail: NavigationDetailInfo) {
    return this.getFiltersFromNavigationDetail(detail).filter((x) => x.dataTypeId === 5 && x.value?.length > 0);
  }

  private getFiltersFromNavigationDetail(
    detail: NavigationDetailInfo
  ): { name: LocalizedString; value: any; dataTypeId: number; visible: boolean }[] {
    return detail.properties
      .map((property) => ({
        name: property.name,
        visible: property.visible,
        dataTypeId: property.propertyTypeId,
        value: this.getValueFromProperty(property),
      }))
      .filter((x) => x.value);
  }

  protected getValueFromProperty(property: AssistPropertyBM & { propertyTypeId: number; valueChoices: any[] }) {
    switch (property.propertyTypeId) {
      case 1: {
        return {
          value: property.valueBoolean,
          imageUrl: property.file,
        };
      }
      case 2: {
        return {
          value: property.valueNumber,
          imageUrl: property.file,
        };
      }
      case 3: {
        return {
          value: property.valueDecimal,
          imageUrl: property.file,
        };
      }
      case 4: {
        return {
          value: property.valueText,
          imageUrl: property.file,
        };
      }
      case 5: {
        return property.valueChoices.map((x) => ({
          title: x.value,
          imageUrl: x.imageUrl,
        }));
      }
      case 6: {
        return {
          value: property.valueHTMLContent,
          imageUrl: property.file,
        };
      }
      default:
        return null;
    }
  }

  openParentResource(resourceId: number) {
    if (resourceId) {
      // this.parentItemId = this.resourcePoiIdentification().resourceId;
      this.resourcePoiIdentification.set({
        resourceId: resourceId,
        qrCodeId: null,
      });
      this.currentMapItemService?.updateCurrentItemId(resourceId, null);
      this.poiOrResourceDetailOpened.emit({
        resourceId: resourceId,
        qrCodeId: null,
      });
    }
  }

  protected closeDetail() {
    //if there is a parentItemId - the child was opened from parent, do not close detail but go back to parent detail
    const newResourceId =
      this.itemDetail.hasValue() && this.itemDetail.value().resourceId ? this.itemDetail.value().resourceId : null;
    if (newResourceId && newResourceId != this.resourcePoiIdentification().resourceId) {
      this.resourcePoiIdentification.set({
        resourceId: newResourceId,
        qrCodeId: null,
      });
      this.currentMapItemService?.updateCurrentItemId(newResourceId, null);
    } else {
      this.currentMapItemService?.updateCurrentItemId(null, null);
      this.poiOrResourceDetailOpened.emit(null);

      if (this.kioskId()) {
        this.itemDetailResult.emit({
          viewState: DetailViewState.RequestChangeTarget,
        });
      } else {
        let viewState = DetailViewState.RequestClose;

        if (this.openDetailFrom() === OpenDetailFromEnum.StartSelection) {
          viewState = DetailViewState.RequestChangeStart;
        } else if (this.openDetailFrom() === OpenDetailFromEnum.TargetSelection) {
          viewState = DetailViewState.RequestChangeTarget;
        }
        this.itemDetailResult.emit({
          viewState: viewState,
        });
      }
    }
  }

  protected setItemAsStart(data: NavigationDetailInfo) {
    this.currentMapItemService?.updateStartResourcePOI(
      this.isResource() ? data.resourceId : null,
      this.isResource() ? null : data.qrCodeId
    );

    this.itemDetailResult.emit({
      viewState: DetailViewState.RequestChangeStart,
      itemData: {
        ...data,
        resourceId: this.isResource() ? data.resourceId : null,
        qrCode: this.isResource() ? null : data.qrCodeId,
      },
    });
  }

  protected setItemAsTarget(data: NavigationDetailInfo) {
    this.currentMapItemService?.updateTargetResourcePOI(
      this.isResource() ? data.resourceId : null,
      this.isResource() ? null : data.qrCodeId
    );
    this.itemDetailResult.emit({
      viewState: DetailViewState.RequestChangeTarget,
      itemData: {
        ...data,
        resourceId: this.isResource() ? data.resourceId : null,
        qrCode: this.isResource() ? null : data.qrCodeId,
      },
    });
  }

  protected moveToArea(areaId: number, resourceName: LocalizedString, resourceId: number, qrCodeId: string) {
    const isStart = this.startSelectionPriority();
    this.requestMoveToArea.emit({
      source: ChangeAreaSourceEnum.ItemDetail,
      requestedAreaId: areaId,
      resourceName,
      startSelectionPriority: isStart,
      requestNewPOI: qrCodeId,
      requestNewResource: resourceId,
    });
  }

  protected isActiveBreadcrumb(breadcrumbAreaId: number, params: Params) {
    const currentAreaId = params['areaId'];
    return breadcrumbAreaId == currentAreaId;
  }

  getAvailability(schedule: ResourceSchedule | null) {
    return schedule?.availabilityView?.[0];
  }

  getAvailaibilityColor(schedule: ResourceSchedule | null) {
    if (!schedule?.availabilityView?.length) {
      return {};
    }
    let color = '';
    switch (schedule.availabilityView[0]) {
      case '0':
        color = this.navigationSettings.design.background.reservableResource.free;
        break;
      case '1':
        color = this.navigationSettings.design.background.reservableResource.tentative;
        break;
      case '2':
        color = this.navigationSettings.design.background.reservableResource.reserved;
        break;
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
        color = this.navigationSettings.design.background.reservableResource.closed;
        break;
      case '8':
        color = this.navigationSettings.design.background.reservableResource.freeClosed;
        break;
      case '9':
        color = this.navigationSettings.design.background.reservableResource.reserved;
        break;
    }

    return { backgroundColor: this.removeAlphaFromRgba(color), color: this.getTextColorBasedOnBg(color) };
  }

  protected getTextColorBasedOnBg(color?: string): string {
    if (!color) return '#212529';

    const [r, g, b] = (color.match(/\d+/g) || []).map(Number);
    if (r === undefined || g === undefined || b === undefined) return '#212529';

    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 140 ? '#212529' : '#ffffff';
  }

  removeAlphaFromRgba(color: string): string {
    const rgbaPattern = /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*[\d.]+\)$/;
    const match = color.match(rgbaPattern);

    // je to RGBA
    if (match) {
      const r = match[1];
      const g = match[2];
      const b = match[3];
      return `rgba(${r}, ${g}, ${b})`;
    } else {
      //neni RGBA vrat primo barvu
      return color;
    }
  }

  availabilityTextMap = {
    '0': translation.availability.free,
    '1': translation.availability.tentative,
    '2': translation.availability.reserved,
    '3': translation.availability.notReservableNow,
    '4': translation.availability.notReservableNow,
    '5': translation.availability.notReservableNow,
    '6': translation.availability.notReservableNow,
    '7': translation.availability.notReservableNow,
    '9': translation.availability.reserved,
    '8': translation.availability.freeClosed,
  };

  getAvailaibilityText(schedule: ResourceSchedule | null) {
    const availability = this.getAvailability(schedule);
    return this.availabilityTextMap[availability];
  }

  protected mapItemName(mapItemDetail: NavigationDetailInfo): string {
    if (localized(mapItemDetail.displayNameForApp, this.translocoService) && this.isResource()) {
      return localized(mapItemDetail.displayNameForApp, this.translocoService);
    }
    return localized(mapItemDetail.name, this.translocoService);
  }

  protected shareDetail() {
    const testShareData = {
      title: 'CUE Navigation',
      text: '',
      url: window.location.href,
    };

    if (navigator.canShare(testShareData)) {
      navigator
        .share(testShareData)
        .then(() => console.log('Úspěšně sdíleno'))
        .catch((error) => console.error('Chyba při sdílení:', error));
    } else {
      this.copyToClipboard(testShareData.url);
    }
  }

  private copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log('Odkaz byl zkopírován do schránky');
        alert('Odkaz byl zkopírován do schránky');
      })
      .catch((error) => {
        console.error('Chyba při kopírování:', error);
      });
  }
}
